import { useMemo } from 'react';
import { useApiOptions } from '../../hooks/useApiOptions';
import type { EFormFieldSchema } from './schema';

/**
 * Curated reference-data sources for E-Form select/radio fields.
 * Keys are stored on the field schema; endpoints must already exist in the API.
 *
 * Paginated list endpoints accept `searchParams` (and usually `search`) for filtering.
 * Field renderers currently load options once via `apiEndpoint` and filter client-side
 * in the Select search box — so we request a large page rather than silently truncating
 * at 500. Prefer `page(path)` for large catalogs; lookup tables omit pagination.
 */

export type EFormReferenceSource = {
  key: string;
  label: string;
  category: string;
  endpoint: string;
  valueField: string;
  labelField: string;
};

/** Build a list endpoint URL. Default limit is high to avoid silent truncation. */
const page = (path: string, limit = 2000) => `${path}?page=1&limit=${limit}`;

/**
 * Append or replace a search query for endpoints that support `searchParams`.
 * Useful if a caller builds a searchable endpoint dynamically.
 */
export function eformReferenceEndpointWithSearch(
  endpoint: string,
  search: string,
): string {
  const q = search.trim();
  if (!q) return endpoint;
  try {
    const url = new URL(endpoint, 'http://local.invalid');
    url.searchParams.set('searchParams', q);
    url.searchParams.set('page', url.searchParams.get('page') || '1');
    if (!url.searchParams.has('limit')) url.searchParams.set('limit', '2000');
    return `${url.pathname}?${url.searchParams.toString()}`;
  } catch {
    const sep = endpoint.includes('?') ? '&' : '?';
    return `${endpoint}${sep}searchParams=${encodeURIComponent(q)}`;
  }
}

export const EFORM_REFERENCE_SOURCES: EFormReferenceSource[] = [
  // Core
  { key: 'countries', label: 'Countries', category: 'Core', endpoint: page('/countries'), valueField: 'id', labelField: 'name' },
  { key: 'currencies', label: 'Currencies', category: 'Core', endpoint: page('/currencies'), valueField: 'id', labelField: 'name' },
  { key: 'departments', label: 'Departments', category: 'Core', endpoint: page('/departments'), valueField: 'id', labelField: 'name' },
  { key: 'ports', label: 'Ports', category: 'Core', endpoint: page('/ports'), valueField: 'id', labelField: 'name' },
  { key: 'vessels', label: 'Vessels', category: 'Core', endpoint: page('/vessels'), valueField: 'id', labelField: 'vesselName' },
  { key: 'suppliers', label: 'Suppliers', category: 'Core', endpoint: page('/suppliers'), valueField: 'id', labelField: 'name' },
  { key: 'management_companies', label: 'Management companies', category: 'Core', endpoint: page('/management-companies'), valueField: 'id', labelField: 'name' },

  // Crew lookups
  { key: 'crew_ranks', label: 'Ranks', category: 'Crew', endpoint: '/crew-lookups/ranks', valueField: 'id', labelField: 'name' },
  { key: 'crew_nationalities', label: 'Nationalities', category: 'Crew', endpoint: '/crew-lookups/nationalities', valueField: 'id', labelField: 'name' },
  { key: 'crew_sex_types', label: 'Sex types', category: 'Crew', endpoint: '/crew-lookups/sex-types', valueField: 'id', labelField: 'name' },
  { key: 'crew_marital_statuses', label: 'Marital statuses', category: 'Crew', endpoint: '/crew-lookups/marital-statuses', valueField: 'id', labelField: 'name' },
  { key: 'crew_relations', label: 'Relations', category: 'Crew', endpoint: '/crew-lookups/relations', valueField: 'id', labelField: 'name' },
  { key: 'crew_religions', label: 'Religions', category: 'Crew', endpoint: '/crew-lookups/religions', valueField: 'id', labelField: 'name' },
  { key: 'crew_manning_agents', label: 'Manning agents', category: 'Crew', endpoint: '/crew-lookups/manning-agents', valueField: 'id', labelField: 'name' },
  { key: 'crew_availability_statuses', label: 'Availability statuses', category: 'Crew', endpoint: '/crew-lookups/availability-statuses', valueField: 'id', labelField: 'name' },
  { key: 'crew_employment_statuses', label: 'Employment statuses', category: 'Crew', endpoint: '/crew-lookups/employment-statuses', valueField: 'id', labelField: 'name' },
  { key: 'crew_vessel_types', label: 'Vessel types', category: 'Crew', endpoint: '/crew-lookups/vessel-types', valueField: 'id', labelField: 'name' },
  { key: 'crew_engine_types', label: 'Engine types', category: 'Crew', endpoint: '/crew-lookups/engine-types', valueField: 'id', labelField: 'name' },
  { key: 'crew_vessel_owners', label: 'Vessel owners', category: 'Crew', endpoint: '/crew-lookups/vessel-owners', valueField: 'id', labelField: 'name' },
  { key: 'crew_ecdis_types', label: 'ECDIS types', category: 'Crew', endpoint: '/crew-lookups/ecdis-types', valueField: 'id', labelField: 'name' },
  { key: 'crew_sign_off_reasons', label: 'Sign-off reasons', category: 'Crew', endpoint: '/crew-lookups/sign-off-reasons', valueField: 'id', labelField: 'name' },
  { key: 'crew_travel_segment_types', label: 'Travel segment types', category: 'Crew', endpoint: '/crew-lookups/travel-segment-types', valueField: 'id', labelField: 'name' },
  { key: 'crew_cert_categories', label: 'Certificate categories', category: 'Crew', endpoint: '/crew-lookups/cert-categories', valueField: 'id', labelField: 'name' },
  { key: 'crew_cert_catalogs', label: 'Certificate catalog', category: 'Crew', endpoint: '/crew-lookups/cert-catalogs', valueField: 'id', labelField: 'name' },

  // Supplies catalog
  { key: 'store_categories', label: 'Store categories', category: 'Supplies', endpoint: page('/store-categories'), valueField: 'id', labelField: 'name' },
  { key: 'store_units', label: 'Store units', category: 'Supplies', endpoint: page('/store-units'), valueField: 'id', labelField: 'name' },
];

const BY_KEY = new Map(EFORM_REFERENCE_SOURCES.map((s) => [s.key, s]));

export function getEFormReferenceSource(key: string | undefined | null): EFormReferenceSource | undefined {
  if (!key) return undefined;
  return BY_KEY.get(key);
}

export function isEFormReferenceKey(key: unknown): key is string {
  return typeof key === 'string' && BY_KEY.has(key);
}

export function eformReferenceSourcesByCategory(): Array<{ category: string; sources: EFormReferenceSource[] }> {
  const map = new Map<string, EFormReferenceSource[]>();
  for (const s of EFORM_REFERENCE_SOURCES) {
    const list = map.get(s.category) ?? [];
    list.push(s);
    map.set(s.category, list);
  }
  return Array.from(map.entries()).map(([category, sources]) => ({ category, sources }));
}

function isReferenceChoiceField(field: EFormFieldSchema): boolean {
  return (
    (field.type === 'select' || field.type === 'radio' || field.type === 'checklist') &&
    field.optionsSource === 'reference' &&
    Boolean(field.referenceKey)
  );
}

/** value → label maps keyed by reference source key (for list/detail display). */
export function useEFormReferenceLabelMaps(
  fields: EFormFieldSchema[],
): Map<string, Map<string, string>> {
  const endpoints = useMemo(() => {
    const next: Record<string, { endpoint: string; labelField?: string; valueField?: string }> = {};
    for (const field of fields) {
      if (!isReferenceChoiceField(field) || !field.referenceKey) continue;
      if (next[field.referenceKey]) continue;
      const src = getEFormReferenceSource(field.referenceKey);
      if (!src) continue;
      next[field.referenceKey] = {
        endpoint: src.endpoint,
        labelField: src.labelField,
        valueField: src.valueField,
      };
    }
    return next;
  }, [fields]);

  const raw = useApiOptions(endpoints);

  return useMemo(() => {
    const maps = new Map<string, Map<string, string>>();
    for (const [refKey, cfg] of Object.entries(endpoints)) {
      const rows = raw[refKey] ?? [];
      const valueField = cfg.valueField ?? 'id';
      const labelField = cfg.labelField ?? 'name';
      const map = new Map<string, string>();
      for (const item of rows) {
        if (!item || typeof item !== 'object') continue;
        const row = item as Record<string, unknown>;
        const val = row[valueField];
        if (val === undefined || val === null) continue;
        const label = row[labelField];
        map.set(String(val), label != null && String(label).trim() ? String(label) : String(val));
      }
      maps.set(refKey, map);
    }
    return maps;
  }, [endpoints, raw]);
}

export function referenceLabelsForField(
  field: EFormFieldSchema,
  maps: Map<string, Map<string, string>>,
): Map<string, string> | undefined {
  if (!field.referenceKey) return undefined;
  return maps.get(field.referenceKey);
}
